import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

export default function CustomCursor() {
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  const springConfig = { damping: 22, stiffness: 300, mass: 0.3 };
  const x = useSpring(cursorX, springConfig);
  const y = useSpring(cursorY, springConfig);
  const [hovering, setHovering] = useState(false);
  const [clicking, setClicking] = useState(false);
  const [ripples, setRipples] = useState([]);
  const idRef = useRef(0);

  useEffect(() => {
    const move = (e) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
    };
    const down = () => setClicking(true);
    const up = () => setClicking(false);
    const over = (e) => {
      if (e.target.closest("a, button, [role='button'], input, textarea, [data-cursor-hover]")) {
        setHovering(true);
      } else {
        setHovering(false);
      }
    };
    const click = (e) => {
      const id = idRef.current++;
      setRipples((prev) => [...prev, { id, x: e.clientX, y: e.clientY }]);
      setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== id));
      }, 700);
    };

    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("mouseover", over, { passive: true });
    window.addEventListener("mousedown", down);
    window.addEventListener("mouseup", up);
    window.addEventListener("click", click);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("click", click);
    };
  }, [cursorX, cursorY]);

  return (
    <>
      {/* Click ripple */}
      {ripples.map((r) => (
        <motion.div
          key={r.id}
          className="fixed pointer-events-none z-[9998]"
          style={{ left: r.x, top: r.y, translateX: "-50%", translateY: "-50%" }}
          initial={{ width: 0, height: 0, opacity: 0.6 }}
          animate={{ width: 60, height: 60, opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <div className="w-full h-full rounded-full border border-[#F47B20]/50" />
        </motion.div>
      ))}

      {/* Crosshair ring + lines */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-[9999] mix-blend-difference"
        style={{
          x,
          y,
          translateX: "-50%",
          translateY: "-50%",
          willChange: "transform",
        }}
        animate={{
          scale: clicking ? 0.7 : hovering ? 1.5 : 1,
          rotate: hovering ? 45 : 0,
        }}
        transition={{ type: "spring", stiffness: 250, damping: 22 }}
        data-testid="custom-cursor"
      >
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle
            cx="24" cy="24" r="14"
            stroke="#F47B20"
            strokeWidth="1"
            opacity={hovering ? 0.2 : 0.5}
          />
          {/* Top */}
          <line x1="24" y1="4" x2="24" y2="12" stroke="#F47B20" strokeWidth="1.5" />
          {/* Bottom */}
          <line x1="24" y1="36" x2="24" y2="44" stroke="#F47B20" strokeWidth="1.5" />
          {/* Left */}
          <line x1="4" y1="24" x2="12" y2="24" stroke="#F47B20" strokeWidth="1.5" />
          {/* Right */}
          <line x1="36" y1="24" x2="44" y2="24" stroke="#F47B20" strokeWidth="1.5" />
          {/* Corner ticks on hover */}
          {hovering && (
            <>
              <line x1="8" y1="8" x2="13" y2="13" stroke="#F47B20" strokeWidth="1" opacity="0.6" />
              <line x1="40" y1="8" x2="35" y2="13" stroke="#F47B20" strokeWidth="1" opacity="0.6" />
              <line x1="8" y1="40" x2="13" y2="35" stroke="#F47B20" strokeWidth="1" opacity="0.6" />
              <line x1="40" y1="40" x2="35" y2="35" stroke="#F47B20" strokeWidth="1" opacity="0.6" />
            </>
          )}
        </svg>
      </motion.div>

      {/* Center dot — same spring as crosshair so they stay locked */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-[9999]"
        style={{
          x,
          y,
          translateX: "-50%",
          translateY: "-50%",
          willChange: "transform",
        }}
        animate={{
          scale: clicking ? 0.4 : hovering ? 0 : 1,
        }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <div
          className="rounded-full bg-[#F47B20]"
          style={{
            width: 6,
            height: 6,
            boxShadow: "0 0 12px #F47B20, 0 0 30px rgba(244,123,32,0.3)",
          }}
        />
      </motion.div>
    </>
  );
}
